/* eslint-env browser */

const CHATBOT_GATEWAY_PATH = /\/merchant-service\/chatbot\/services\/chatbot-service\/(?:getConversationId|getHistory|sendMessage|faq|handover\/(?:callMeBack|callMeNow|requestChat|sendMessage|refreshChat))\/?$/

const CHATBOT_PROXY_PATH = /^\/(?:api\/(?:token|history|conversations\/[^/]+(?:\/(?:history|activities))?)|v3\/directline\/(?:tokens\/refresh|conversations(?:\/[^/]+(?:\/(?:activities(?:\/[^/]+)?|upload))?)?))\/?$/

const subscribers = new Map()
const requestUrls = new WeakMap()
let originalOpen
let originalSend
let observedOpen
let observedSend
let originalFetch
let observedFetch

export function isChatbotGatewayRequest (url) {
  try {
    const { pathname } = new URL(url, window.location.origin)
    return CHATBOT_GATEWAY_PATH.test(pathname)
  } catch {
    return false
  }
}

function isChatbotProxyRequest (url, proxyBaseUrl) {
  if (!proxyBaseUrl) return false
  try {
    const request = new URL(url, window.location.origin)
    const base = new URL(proxyBaseUrl, window.location.origin)
    const basePath = base.pathname.replace(/\/+$/, '')
    return request.origin === base.origin &&
      request.pathname.startsWith(`${basePath}/`) &&
      CHATBOT_PROXY_PATH.test(request.pathname.slice(basePath.length))
  } catch {
    return false
  }
}

function notifySubscribers (url, legacy = false) {
  subscribers.forEach(({ proxyBaseUrl }, onActivity) => {
    if (legacy || isChatbotProxyRequest(url, proxyBaseUrl)) {
      try {
        onActivity()
      } catch {
        // The external chatbot request must not be affected by app-side observers.
      }
    }
  })
}

function isSuccessful (status) {
  return status >= 200 && status < 300
}

function installObserver () {
  if (!observedFetch && typeof window.fetch === 'function') {
    originalFetch = window.fetch
    const fetch = originalFetch
    observedFetch = function (input, ...args) {
      const url = input && typeof input === 'object' && 'url' in input ? input.url : input
      const result = fetch.call(this, input, ...args)
      result.then(response => {
        if (isSuccessful(response.status)) notifySubscribers(url)
      }, () => {})
      return result
    }
    window.fetch = observedFetch
  }

  if (observedOpen || typeof XMLHttpRequest === 'undefined') {
    return
  }

  originalOpen = XMLHttpRequest.prototype.open
  originalSend = XMLHttpRequest.prototype.send

  observedOpen = function (method, url, ...args) {
    requestUrls.set(this, url)
    return originalOpen.call(this, method, url, ...args)
  }

  observedSend = function (...args) {
    const isGatewayRequest = isChatbotGatewayRequest(requestUrls.get(this))
    const url = requestUrls.get(this)
    if (!isGatewayRequest && [...subscribers.values()].some(({ proxyBaseUrl }) => isChatbotProxyRequest(url, proxyBaseUrl))) {
      this.addEventListener('load', () => {
        if (isSuccessful(this.status)) notifySubscribers(url)
      }, { once: true })
    }
    const result = originalSend.apply(this, args)
    if (isGatewayRequest) {
      notifySubscribers(url, true)
    }
    return result
  }

  XMLHttpRequest.prototype.open = observedOpen
  XMLHttpRequest.prototype.send = observedSend
}

function uninstallObserver () {
  if (observedFetch && window.fetch === observedFetch) {
    window.fetch = originalFetch
    originalFetch = null
    observedFetch = null
  }

  if (!observedOpen || typeof XMLHttpRequest === 'undefined') {
    return
  }

  if (XMLHttpRequest.prototype.open !== observedOpen || XMLHttpRequest.prototype.send !== observedSend) {
    return
  }

  XMLHttpRequest.prototype.open = originalOpen
  XMLHttpRequest.prototype.send = originalSend
  originalOpen = null
  originalSend = null
  observedOpen = null
  observedSend = null
}

export function observeChatbotGatewayActivity (onActivity, proxyBaseUrl) {
  installObserver()
  const subscription = subscribers.get(onActivity)
  subscribers.set(onActivity, { references: (subscription?.references || 0) + 1, proxyBaseUrl })
  let active = true

  return () => {
    if (!active) {
      if (subscribers.size === 0) uninstallObserver()
      return
    }
    active = false
    const subscription = subscribers.get(onActivity)
    const references = subscription?.references

    if (references === 1) {
      subscribers.delete(onActivity)
    } else if (references) {
      subscription.references -= 1
    }

    if (subscribers.size === 0) {
      uninstallObserver()
    }
  }
}
